import {
  breakpointSizes,
  getBreakpointOnly,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React from 'react'
import { useSelector } from 'react-redux'
import AccountSummaryTooltip from 'src/components/AccountSummaryTooltip/AccountSummaryTooltip'
import Box from 'src/components/Box/Box'
import ComdataAvailableLimitTooltip from 'src/components/ComdataAvailableLimitTooltip/ComdataAvailableLimitTooltip'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import NavAwayBox from 'src/components/NavAwayBox/NavAwayBox'
import PhysicalCardInfoTooltip from 'src/components/PhysicalCardInfoTooltip/PhysicalCardInfoTooltip'
import SvgArrowForward from 'src/components/Svgs/SvgArrowForward'
import { Issuers, RegionsIssuers } from 'src/enums'
import Crncy from 'src/localization/Crncy'
import Trns from 'src/localization/Trns'
import {
  CreditCardListModel,
  CreditCardType,
} from 'src/models/creditCard.models'
import { accountSettingsUrl, accountVirtualCardsUrl } from 'src/navigation/urls'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import { css } from 'styled-components'
interface AccountStatusBoxProps {
  creditCard: CreditCardListModel
}

const AccountStatusBox: React.FC<AccountStatusBoxProps> = (
  props,
): React.ReactElement => {
  const user = useSelector(selectUserListModelFromState)

  const isLimitedBudgetWithSentCards =
    props.creditCard.isLimitedBudget && props.creditCard.unspentAmountCents > 0

  const topElement = (
    <>
      <div
        css={css`
          display: flex;
          flex-direction: column;
          border-top-left-radius: 10px;
          border-top-right-radius: 10px;
          background-color: ${styleConstants.colors.white};
          padding: 36px 40px;

          ${getBreakpointOnly(breakpointSizes.XXSM)(css`
            padding: 36px 20px;
          `)}
        `}
      >
        <div
          css={css`
            display: flex;
            align-items: center;
            justify-content: space-between;
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: flex-end;
            `}
          >
            <span
              css={css`
                ${getFontCss(
                  20,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.medium,
                )};
              `}
            >
              <Trns source={'Summary'} />
            </span>
            {props.creditCard.isBudget &&
              (props.creditCard.isInactive || props.creditCard.isClosed) && (
                <span
                  css={css`
                    margin-left: 20px;
                    ${getFontCss(
                      20,
                      styleConstants.colors.red,
                      styleConstants.fontFamilies.regular,
                    )};
                  `}
                >
                  <Trns source={'Inactive'} />
                </span>
              )}
            {!props.creditCard.isLimitedBudget &&
              (props.creditCard.isActive || props.creditCard.isPending) && (
                <span
                  css={css`
                    margin-left: 10px;
                    ${getFontCss(
                      14,
                      themeConstants.colors.highlight,
                      styleConstants.fontFamilies.medium,
                    )};
                  `}
                >
                  <Trns source={'Active Cards'} />
                </span>
              )}
            <AccountSummaryTooltip
              openLeft={false}
              isBudget={props.creditCard.isBudget}
              isLimitedBudget={props.creditCard.isLimitedBudget}
            />
          </div>
          {!props.creditCard.isBudget && (
            <HoverableLink
              css={css`
                margin-top: 6px;

                ${getBreakpointOnly(breakpointSizes.XSM)(css`
                  display: none;
                `)}
              `}
              to={accountSettingsUrl(props.creditCard.id)}
              ariaLabelSource={'Account Settings'}
              hoverUnderline
            >
              <Trns source={'Account Settings'} />
            </HoverableLink>
          )}
        </div>
        <div
          css={css`
            margin-top: 30px;
            display: flex;
            flex-direction: column;
          `}
        >
          {props.creditCard.isLimitedBudget && (
            <span
              css={css`
                ${getFontCss(
                  14,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.medium,
                )};
                margin-bottom: 10px;
              `}
            >
              <Trns source={'Current budget'} />
            </span>
          )}
          <div
            css={css`
              display: flex;
              align-items: center;
              height: 15px;
              border-radius: 10px;
              overflow: hidden;
            `}
          >
            {!props.creditCard.isLimitedBudget && (
              <>
                <div
                  css={css`
                    height: 15px;
                    background-color: ${themeConstants.colors.font_primary};
                  `}
                  style={{
                    width:
                      props.creditCard.activeIssuedAmountCents > 0
                        ? `${((props.creditCard.activeIssuedAmountCents - props.creditCard.totalActiveSpentCents) * 100) / props.creditCard.activeIssuedAmountCents}%`
                        : `0%`,
                  }}
                />
                <div
                  css={css`
                    height: 15px;
                    background-color: ${styleConstants.colors.gray_light};
                  `}
                  style={{
                    width:
                      props.creditCard.activeIssuedAmountCents > 0
                        ? `${(props.creditCard.totalActiveSpentCents * 100) / props.creditCard.activeIssuedAmountCents}%`
                        : `100%`,
                  }}
                />
              </>
            )}
            {props.creditCard.isLimitedBudget &&
              props.creditCard.issuingLimitCents !== undefined && (
                <>
                  <div
                    css={css`
                      height: 15px;
                      background-color: ${themeConstants.colors.highlight};

                      ${(props.creditCard.isInactive ||
                        props.creditCard.isClosed) &&
                      css`
                        background-color: ${styleConstants.colors.gray_dark};
                      `}
                    `}
                    style={{
                      width:
                        props.creditCard.issuingLimitCents > 0
                          ? `${((props.creditCard.issuingLimitCents - props.creditCard.issuedAmountCents) * 100) / props.creditCard.issuingLimitCents}%`
                          : `0%`,
                    }}
                  />
                  <div
                    css={css`
                      height: 15px;
                      background-color: ${styleConstants.colors.gray_light};
                    `}
                    style={{
                      width:
                        props.creditCard.issuingLimitCents > 0
                          ? `${(props.creditCard.issuedAmountCents * 100) / props.creditCard.issuingLimitCents}%`
                          : `100%`,
                    }}
                  />
                </>
              )}
          </div>
        </div>
        <div
          css={css`
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            margin-top: 20px;
          `}
        >
          {!props.creditCard.isLimitedBudget && (
            <>
              <div
                css={css`
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                `}
              >
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                  `}
                >
                  <div
                    css={css`
                      height: 10px;
                      width: 10px;
                      border-radius: 50%;
                      margin-right: 8px;
                      background-color: ${themeConstants.colors.font_primary};
                    `}
                  />
                  <span
                    css={css`
                      ${getFontCss(
                        14,
                        styleConstants.colors.gray_darkest,
                        styleConstants.fontFamilies.regular,
                      )};
                    `}
                  >
                    <Trns source={'Current Exposure'} />
                  </span>
                </div>
                <span>{props.creditCard.unspentAmountLabel}</span>
              </div>
            </>
          )}
          {props.creditCard.isLimitedBudget && (
            <>
              <div
                css={css`
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                `}
              >
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                  `}
                >
                  <div
                    css={css`
                      height: 10px;
                      width: 10px;
                      border-radius: 50%;
                      margin-right: 8px;
                      background-color: ${themeConstants.colors.highlight};

                      ${(props.creditCard.isInactive ||
                        props.creditCard.isClosed) &&
                      css`
                        background-color: ${styleConstants.colors.gray_dark};
                      `}
                    `}
                  />
                  <span
                    css={css`
                      ${getFontCss(
                        14,
                        styleConstants.colors.gray_darkest,
                        styleConstants.fontFamilies.regular,
                      )};
                    `}
                  >
                    <Trns source={'Remaining'} />
                  </span>
                </div>
                {props.creditCard.unissuedAmount !== undefined && (
                  <span
                    css={css`
                      ${getFontCss(
                        16,
                        themeConstants.colors.highlight,
                        styleConstants.fontFamilies.regular,
                      )};
                      ${(props.creditCard.isInactive ||
                        props.creditCard.isClosed) &&
                      css`
                        ${getFontCss(
                          16,
                          styleConstants.colors.gray_dark,
                          styleConstants.fontFamilies.regular,
                        )};
                      `}
                    `}
                  >
                    {/* TODO: put this on the model... doesn't line up with unissuedAmountLabel */}
                    <Crncy
                      currency={props.creditCard.currency}
                      amount={props.creditCard.unissuedAmount}
                    />
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      {(isLimitedBudgetWithSentCards ||
        (props.creditCard.issuerAccountBalanceCents !== undefined &&
          !props.creditCard.isBudget)) && (
        <div
          css={css`
            border-bottom-left-radius: 10px;
            border-bottom-right-radius: 10px;
            padding: 36px 40px;
            background-color: ${styleConstants.colors.gray_lightest};
            border-top: 1px solid ${styleConstants.colors.gray_light};
            display: flex;
            flex-direction: column;

            ${getBreakpointOnly(breakpointSizes.XXSM)(css`
              padding: 36px 20px;
            `)}
          `}
        >
          <span
            css={css`
              ${getFontCss(
                14,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )};
              display: flex;
              align-items: center;
            `}
          >
            {isLimitedBudgetWithSentCards && <Trns source={'Active Cards'} />}
            {!isLimitedBudgetWithSentCards &&
              props.creditCard.issuer === Issuers.Comdata && (
                <>
                  <Trns source={'Comdata Account Info'} />
                  <ComdataAvailableLimitTooltip openLeft={false} />
                </>
              )}
          </span>
          <div
            css={css`
              display: flex;
              justify-content: space-between;
              margin-top: 10px;
            `}
          >
            <div>
              <span
                css={css`
                  ${getFontCss(
                    14,
                    styleConstants.colors.gray_darkest,
                    styleConstants.fontFamilies.regular,
                  )};
                `}
              >
                {isLimitedBudgetWithSentCards && (
                  <Trns source={'Current Exposure:'} />
                )}
                {!isLimitedBudgetWithSentCards && (
                  <Trns source={'Available Limit:'} />
                )}
              </span>
              <span
                css={css`
                  margin-left: 4px;
                  ${getFontCss(
                    16,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.regular,
                  )};
                `}
              >
                {isLimitedBudgetWithSentCards
                  ? props.creditCard.unspentAmountLabel
                  : props.creditCard.issuerAccountBalanceLabel}
              </span>
            </div>
            <HoverableLink
              to={accountVirtualCardsUrl(props.creditCard.id)}
              css={css`
                display: flex;
                align-items: center;
                ${getBreakpointOnly(breakpointSizes.XSM)(css`
                  display: none;
                `)}
              `}
              ariaLabelSource={'View Virtual Cards'}
            >
              <Trns source={'View Virtual Cards'} />
              <SvgArrowForward
                css={css`
                  transform: scale(calc(18 / 24));
                `}
              />
            </HoverableLink>
          </div>
        </div>
      )}
    </>
  )

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
      `}
    >
      {Object.values(RegionsIssuers).includes(
        props.creditCard.issuer as RegionsIssuers,
      ) &&
      props.creditCard.type === CreditCardType.Source &&
      user?.email.includes('@paywithextend.com') &&
      props.creditCard.issuerPhysicalAvailableBalanceCents ? (
        <Box noPadding>
          {topElement}
          <div
            css={css`
              border-top: 1px solid ${styleConstants.colors.gray_light};
              background-color: ${styleConstants.colors.gray_lightest};
              border-bottom-left-radius: 10px;
              border-bottom-right-radius: 10px;
              padding: 30px 40px;

              ${getBreakpointOnly(breakpointSizes.XXSM)(css`
                padding: 36px 20px;
              `)}
            `}
          >
            <div
              css={css`
                display: flex;
                align-items: center;
                margin-bottom: 8px;
              `}
            >
              <div
                css={css`
                  ${getFontCss(
                    20,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.medium,
                  )};
                `}
              >
                <Trns source="Physical Card Info" />
              </div>
              <PhysicalCardInfoTooltip openLeft={false} />
            </div>
            <div
              css={css`
                display: flex;
                align-items: center;
                justify-content: space-between;
              `}
            >
              <div
                css={css`
                  display: flex;
                  align-items: flex-end;
                `}
              >
                <div
                  css={css`
                    ${getFontCss(
                      14,
                      styleConstants.colors.gray_darkest,
                      styleConstants.fontFamilies.regular,
                    )};
                    margin-right: 5px;
                  `}
                >
                  <Trns source="Available Credit: " />
                </div>
                <div
                  css={css`
                    ${getFontCss(
                      20,
                      themeConstants.colors.font_primary,
                      styleConstants.fontFamilies.medium,
                    )};
                  `}
                >
                  {props.creditCard.issuerPhysicalAvailableBalanceCentsLabel}
                </div>
              </div>
              <HoverableLink
                to={accountVirtualCardsUrl(props.creditCard.id)}
                ariaLabelSource={'View Virtual Cards'}
                hoverUnderline
                css={css`
                  display: flex;
                  justify-content: flex-end;
                  align-items: center;
                `}
              >
                <Trns source={'View Virtual Cards'} />
                <span
                  css={css`
                    transform: scale(calc(18 / 24));
                    transition: transform 100ms ease-out;
                  `}
                >
                  <SvgArrowForward />
                </span>
              </HoverableLink>
            </div>
          </div>
        </Box>
      ) : (
        <NavAwayBox
          navAway={
            isLimitedBudgetWithSentCards ||
            (props.creditCard.issuerAccountBalanceCents !== undefined &&
              !props.creditCard.isBudget)
              ? undefined
              : {
                  to: accountVirtualCardsUrl(props.creditCard.id),
                  label: <Trns source={'View Virtual Cards'} />,
                  ariaLabelSource: 'View Virtual Cards',
                }
          }
        >
          {topElement}
        </NavAwayBox>
      )}
    </div>
  )
}

export default AccountStatusBox
